 
import { useState, useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { TenantRead } from "@harmonyso/tenants-sdk";

interface TenantCreationChartProps {
  tenants: TenantRead[];
}

type ViewMode = "monthly" | "yearly";

export function TenantCreationChart({ tenants }: TenantCreationChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("monthly");
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const chartData = useMemo(() => {
    if (!tenants.length) return [];

    // Group tenants by creation date
    const grouped = new Map<string, number>();

    tenants.forEach((tenant) => {
      const date = new Date(tenant.created_at);
      const year = date.getFullYear();
      const month = date.getMonth();

      if (viewMode === "yearly") {
        const key = year.toString();
        grouped.set(key, (grouped.get(key) ?? 0) + 1);
      } else {
        // Monthly view
        if (selectedYear === null || year === selectedYear) {
          const key = `${String(month + 1).padStart(2, '0')}/${String(year).slice(-2)}`;
          grouped.set(key, (grouped.get(key) ?? 0) + 1);
        }
      }
    });

    // Convert to array and sort
    return Array.from(grouped.entries())
      .map(([period, count]) => ({
        period,
        count,
      }))
      .sort((a, b) => {
        if (viewMode === "yearly") {
          return parseInt(a.period) - parseInt(b.period);
        } else {
          // For monthly data, parse MM/YY format
          const [monthA, yearA] = a.period.split('/');
          const [monthB, yearB] = b.period.split('/');
          const dateA = new Date(2000 + parseInt(yearA), parseInt(monthA) - 1);
          const dateB = new Date(2000 + parseInt(yearB), parseInt(monthB) - 1);
          return dateA.getTime() - dateB.getTime();
        }
      });
  }, [tenants, viewMode, selectedYear]);

  const availableYears = useMemo(() => {
    return [...new Set(tenants.map(tenant =>
      new Date(tenant.created_at).getFullYear()
    ))].sort((a, b) => b - a);
  }, [tenants]);

  const currentYear = new Date().getFullYear();
  const displayYear = selectedYear ?? currentYear;

  const chartConfig = {
    count: {
      label: "Tenants Created",
      color: "#2563eb",
    },
  } satisfies ChartConfig;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base font-medium">
            Tenant Creation {viewMode === "monthly" ? `- ${displayYear}` : "by Year"}
          </CardTitle>
          <CardDescription>
            {viewMode === "monthly"
              ? `Monthly tenant creation for ${displayYear}`
              : "Total tenants created per year"
            }
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {viewMode === "monthly" && availableYears.length > 1 && (
            <div className="flex gap-1">
              {availableYears.map(year => (
                <Button
                  key={year}
                  variant={selectedYear === year ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSelectedYear(year);
                  }}
                  className="h-7 px-2 text-xs"
                >
                  {year}
                </Button>
              ))}
            </div>
          )}
          <Button
            variant={viewMode === "monthly" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setViewMode("monthly");
            }}
            className="h-7 px-2 text-xs"
          >
            Monthly
          </Button>
          <Button
            variant={viewMode === "yearly" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setViewMode("yearly");
            }}
            className="h-7 px-2 text-xs"
          >
            Yearly
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
          <BarChart accessibilityLayer data={chartData} margin={{ left: -20, right: 12, top: 12, bottom: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="period"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value) => String(value)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar
              dataKey="count"
              fill="var(--color-count)"
              radius={4}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}